import type { OrderApiResponse } from 'src/libs/api/response/OrderApiResponse';
import type { CompletionDetail } from 'src/models/attributionTokens/schema';
import type { BrandSchema } from 'src/models/brand/schema';
import type { CartProductsSchema } from 'src/models/cart/CartProductsSchema';
import type { MakerSchema } from 'src/models/maker/schema';
import type { Product } from 'src/models/product/schema';
import { getBrandName, getMakerName } from 'src/utils/productHelper';
import { isWechat } from './wechat';

type OrderItem = {
  item_name: string;
  item_id: number;
  item_brand: string | undefined;
  item_category: string | undefined;
  price: number;
  quantity: number;
};

type DataLayerForSingleProduct = {
  event: string;
  ecommerce: {
    items: OrderItem[];
  };
};

type DataLayerForBeginCheckout = {
  event: string;
  ecommerce: {
    items: OrderItem[];
  };
};

type DataLayerForPurchase = {
  event: string;
  ecommerce: {
    transaction_id: string;
    affiliation: string;
    value: number;
    currency: 'JPY';
    coupon: string;
    items: OrderItem[];
  };
};

type DataLayerForAffiliatesOnePurchase = {
  event: string;
  eventModel: {
    value: number;
    transaction_id: string;
  };
};

export type CloudRetailProduct = {
  product: {
    id: string;
  };
  quantity?: number;
};

type DataLayerForCloudRetail = {
  eventType: string;
  attributionToken?: string;
  searchQuery?: string;
  productDetails: CloudRetailProduct[];
  completionDetail?: CompletionDetail;
};

type DataLayer =
  | DataLayerForSingleProduct
  | DataLayerForBeginCheckout
  | DataLayerForPurchase
  | DataLayerForAffiliatesOnePurchase
  | DataLayerForCloudRetail;

const initAndPushDataLayer = (data: DataLayer) => {
  if (isWechat()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null, eventModel: null });
  window.dataLayer.push(data);
};

export const getCookieValue = (cookieName: string): string => {
  const cookies = document.cookie.split('; ').map((cookie) => cookie.split('='));
  const targetCookie = cookies.find(([name]) => name === cookieName);
  return targetCookie ? targetCookie[1] : '';
};

export const initAndPushCloudRetailDataLayer = ({
  eventType,
  searchQuery,
  attributionToken,
  productDetails,
  completionDetail
}: DataLayerForCloudRetail) => {
  const visitorId = getCookieValue('_ga');

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    cloud_retail: {
      eventType,
      visitorId,
      ...(searchQuery && { searchQuery }),
      attributionToken,
      productDetails,
      ...(completionDetail && { completionDetail })
    }
  });
};

// 商品詳細表示・カート追加イベント用
export const pushDataLayerForSingleProduct = (
  event: string,
  product: Product,
  brandName: string,
  categoryName: string,
  quantity: number = 1 //商品詳細表示時の数量は1にする
) => {
  const dataLayerObject: DataLayerForSingleProduct = {
    event,
    ecommerce: {
      items: [
        {
          item_name: product.name,
          item_id: product.productId,
          item_brand: brandName,
          item_category: categoryName,
          price: product.undiscountedPrice,
          quantity: quantity
        }
      ]
    }
  };
  initAndPushDataLayer(dataLayerObject);
};

// 決済開始イベント用
export const pushDataLayerForBeginCheckout = (
  event: string,
  products: CartProductsSchema[],
  makers: MakerSchema[],
  brands: BrandSchema[]
) => {
  const dataLayerObject: DataLayerForBeginCheckout = {
    event,
    ecommerce: {
      items: products.map((product) => ({
        item_name: product.name,
        item_id: product.productId,
        item_brand: getMakerName(product.makerId || 0, makers),
        item_category: getBrandName(product.brandId || 0, brands),
        price: product.undiscountedPrice,
        quantity: product.quantity
      }))
    }
  };
  initAndPushDataLayer(dataLayerObject);
};

// 購入完了イベント用
export const pushDataLayerForPurchase = (order: OrderApiResponse, affiliation: string) => {
  // クーポン名を取得(空文字の場合もあり)、クーポン利用が一つもない場合は'not set'を設定
  const couponLabels = order.vendorAmounts.map((vendor) =>
    'discount' in vendor && 'couponLabel' in vendor.discount ? vendor.discount.couponLabel : undefined
  );
  const coupon = couponLabels.some((label) => label !== undefined) ? couponLabels.join(',') : 'not set';
  const dataLayerObject: DataLayerForPurchase = {
    event: 'purchase',
    ecommerce: {
      transaction_id: order.orderId,
      affiliation: affiliation,
      value: order.totalAmount,
      currency: 'JPY',
      coupon: coupon,
      items: order.lineItems.map((item) => ({
        item_name: item.product.name,
        item_id: item.productId,
        item_brand: item.product.maker?.name,
        item_category: item.product.brand?.name,
        price: item.unitPrice,
        quantity: item.quantity
      }))
    }
  };
  initAndPushDataLayer(dataLayerObject);
};

export const pushDataLayerForAffiliatesOnePurchase = (order: OrderApiResponse) => {
  const dataLayerObject: DataLayerForAffiliatesOnePurchase = {
    event: 'purchase',
    eventModel: {
      value: order.paymentAmount,
      transaction_id: order.orderId
    }
  };
  initAndPushDataLayer(dataLayerObject);
};
